import classnames from 'classnames';
import {
  Conversation as ConversationT,
  MetaConversation,
  Tag,
} from 'lib-shared-legacy-types';
import { Sentry } from 'lib-shared-sentry';
import type {
  Conversation as ConversationType,
  User,
} from 'lib-shared-types-api';
import _isEqual from 'lodash/isEqual';
import { memo, useCallback, useEffect } from 'react';

import {
  getConversationTitle,
  getHumanConversationChannel,
} from 'app/utils/conversationTitle';
import { ActivityLog } from 'components/activityLog/components';
import {
  ConversationContextProvider,
  ConversationStatusColorBox,
} from 'components/conversation';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import ConversationSidebar from '../containers/conversationSidebar';
import MessageSection from '../containers/messageSection';
import ReplySection from '../containers/replySection';
import Timeline from '../containers/timeline';
import { canClaimConversation } from '../utils/canClaimConversation';
import { canTakeOverConversation } from '../utils/canTakeOverConversation';

import { ConversationSummary } from './conversationSummary';
import { ConversationHeader } from './header';
import { SubConversationList } from './subConversationList';
import { SubConversationListItem } from './subConversationList/subConversationList';

import '../styles/conversation.less';

type ConversationProps = {
  metaConversation: MetaConversation;
  latestMessengerConversation: any;
  conversationClaim: any;
  telephonyMakeCall: any;
  composeEmailOpenModal: any;
  asyncStatus: any;
  isMenuOpen: boolean;
  isCustomCardMenuOpen: boolean;
  conversationUnmount: any;
  conversationResetUnread: (payload: any) => void;
  conversationGet: (payload: any) => Promise<any>;
  notificationTabBadgeReset: () => void;
  isActivityLogOpen: boolean;
  isFocusMode: boolean;
  closeSideBarMenu: () => void;
};

const getConversationRequesterDetails = (requester: User | null) =>
  requester?.name || requester?.email || requester?.phone_number || undefined;

function Conversation(props: ConversationProps): JSX.Element {
  const params = useAppPathParams('/conversation/:csid');
  const csid: number = parseInt(params.csid, 10);

  const navigate = useAppNavigate();

  const {
    metaConversation,
    latestMessengerConversation,
    isCustomCardMenuOpen,
    isFocusMode,
    conversationResetUnread,
    conversationGet,
    notificationTabBadgeReset,
    conversationUnmount,
  } = props;
  const {
    parentConversation,
    parentConversationRequester,
    linkedConversations,
    conversation,
    isAssignedToMe,
  } = metaConversation;

  const prepConversationByCSID = useCallback(
    (conversationId: number) => {
      conversationResetUnread({ csid: conversationId });
      conversationGet({ csid: conversationId }).catch(Sentry.captureException);
      notificationTabBadgeReset();
    },
    [conversationResetUnread, conversationGet, notificationTabBadgeReset],
  );

  useEffect(() => {
    prepConversationByCSID(csid);
    return () => conversationUnmount();
    // This should only be called when the csid is changed,
    // so to be on the safe side we only have csid in the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csid]);

  const showTakeoverButton = canTakeOverConversation(
    conversation,
    isAssignedToMe,
  );
  const showClaimButton = canClaimConversation(
    conversation,
    isAssignedToMe,
    latestMessengerConversation,
  );

  const renderContent = (): JSX.Element => {
    const { isFocusMode } = props;
    const { requester } = props.metaConversation;

    return props.isActivityLogOpen ? (
      <ActivityLog />
    ) : (
      <div
        className={classnames('content message-content scrollable', {
          'focus-mode': isFocusMode,
        })}
        style={{ padding: 0 }}
      >
        <MessageSection />

        {!requester?.anonymized_at && (
          <ReplySection
            showTakeoverButton={showTakeoverButton}
            showClaimButton={showTakeoverButton ? false : showClaimButton}
          />
        )}
      </div>
    );
  };

  return (
    <ConversationContextProvider conversation={metaConversation.conversation}>
      <div className="conversation-view">
        {!parentConversation && (
          <div
            className={classnames('conversation-view__timeline', {
              'conversation-view__timeline--placeholder': !!parentConversation,
              'conversation-view__focus-mode': isFocusMode,
            })}
          >
            <Timeline />
          </div>
        )}
        <div className="conversation-view__main">
          {!!parentConversation && (
            <div
              className="conversation-view__parent"
              onClick={() =>
                navigate({
                  path: '/conversation/:csid',
                  params: {
                    path: {
                      csid: `${parentConversation.id}`,
                    },
                  },
                })
              }
              data-status={parentConversation.status}
            >
              <ConversationStatusColorBox
                className="conversation-status"
                csid={parentConversation.id as ConversationType['id']}
                status={parentConversation.status as ConversationType['status']}
              />

              <ConversationSummary
                className="conversation-view__parent__summary"
                conversationAttributes={
                  metaConversation.conversation.attributes
                }
                conversationInfo={parentConversation}
                labels={{
                  title: getConversationTitle({
                    conversation: parentConversation,
                    requester: parentConversationRequester,
                  }),
                  agent: conversation.assignment?.agent?.name || undefined,
                  requester: getConversationRequesterDetails(
                    parentConversationRequester || null,
                  ),
                  queue: parentConversation.queue?.name,
                  channel: getHumanConversationChannel(
                    parentConversation,
                  ) as any,
                }}
              />
            </div>
          )}
          <div
            data-status={props.metaConversation.conversation.status}
            className="conversation-view__current conversation-messages"
          >
            <ConversationHeader />
            {renderContent()}
          </div>
          {!parentConversation &&
            linkedConversations &&
            linkedConversations.length > 0 && (
              <div className="conversation-view__children">
                <SubConversationList
                  conversations={linkedConversations.map<SubConversationListItem>(
                    (metaC: {
                      conversation: ConversationT;
                      notes: Array<any>;
                      requester: User;
                      tags: Array<Tag>;
                    }) => {
                      return {
                        info: metaC.conversation,
                        requester: metaC.requester,
                        labels: {
                          title: getConversationTitle(metaC),
                          agent: metaC.conversation.assignment?.agent?.name,
                          queue: metaC.conversation.queue?.name,
                          channel: getHumanConversationChannel(
                            metaC.conversation,
                          ),
                        },
                      } as SubConversationListItem;
                    },
                  )}
                  onConversationClick={(csid: number | string) =>
                    navigate({
                      path: '/conversation/:csid',
                      params: {
                        path: {
                          csid: `${csid}`,
                        },
                      },
                    })
                  }
                />
              </div>
            )}
        </div>
        <div
          className={classnames('conversation-view__sidebar', {
            closed: !isCustomCardMenuOpen,
          })}
        >
          <ConversationSidebar />
        </div>

        <div
          className={classnames('conversation-view__backdrop', {
            closed: !isCustomCardMenuOpen,
          })}
          onClick={props.closeSideBarMenu}
        />
      </div>
    </ConversationContextProvider>
  );
}

export default memo(Conversation, _isEqual);
